/**
 * Front-end entry for global styles + small progressive enhancement.
 */
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/space-grotesk/500.css';
import '@fontsource/space-grotesk/600.css';
import '@fontsource/space-grotesk/700.css';
import '@fontsource/jetbrains-mono/400.css';
import '@fontsource/jetbrains-mono/500.css';
import './theme.css';

const reducedMotion = globalThis.matchMedia('(prefers-reduced-motion: reduce)');

type RevealPreset = 'up' | 'left' | 'right' | 'scale' | 'line';

type CounterParts = {
	original: string;
	prefix: string;
	suffix: string;
	target: number;
	decimals: number;
	decimalSeparator: string;
	minDigits: number;
};

const configureMotionNode = (node: HTMLElement, preset: RevealPreset = 'up', delay = 0): void => {
	if (!node.dataset.rtReveal) node.dataset.rtReveal = preset;
	const legacyDelay = Number.parseInt(node.dataset.delay || '', 10);
	const requestedDelay = Number.parseInt(node.dataset.rtDelay || '', 10);
	const normalizedDelay = Number.isFinite(requestedDelay)
		? requestedDelay
		: (Number.isFinite(legacyDelay) ? legacyDelay : delay);
	node.dataset.rtDelay = String(Math.max(0, Math.min(normalizedDelay, 4)));
};

const parseCounter = (node: HTMLElement): CounterParts | null => {
	const original = (node.dataset.rtCounterValue || node.textContent || '').trim();
	if (!original || /\d\s*[–-]\s*\d/.test(original) || /\d\s*\/\s*\d/.test(original)) return null;

	const match = original.match(/^(\+?)(\d+(?:[.,]\d+)?)(.*)$/);
	if (!match) return null;

	const numericText = match[2];
	const separator = numericText.includes(',') ? ',' : (numericText.includes('.') ? '.' : '');
	const fraction = separator ? numericText.split(separator)[1] || '' : '';
	const integer = separator ? numericText.split(separator)[0] : numericText;
	const target = Number.parseFloat(numericText.replace(',', '.'));
	if (!Number.isFinite(target)) return null;

	return {
		original,
		prefix: match[1],
		suffix: match[3],
		target,
		decimals: fraction.length,
		decimalSeparator: separator,
		minDigits: integer.length > 1 && integer.startsWith('0') ? integer.length : 1,
	};
};

const renderCounterValue = (parts: CounterParts, value: number): string => {
	let number = parts.decimals > 0
		? value.toFixed(parts.decimals)
		: Math.round(value).toString().padStart(parts.minDigits, '0');
	if (parts.decimalSeparator === ',') number = number.replace('.', ',');
	return `${parts.prefix}${number}${parts.suffix}`;
};

const animateCounter = (node: HTMLElement): void => {
	if (node.dataset.rtCounted === '1') return;
	const parts = parseCounter(node);
	if (!parts) return;

	node.dataset.rtCounted = '1';
	node.setAttribute('aria-label', parts.original);
	const duration = Math.max(500, Math.min(Number.parseInt(node.dataset.rtDuration || '900', 10), 1600));
	const startedAt = performance.now();

	const tick = (now: number): void => {
		const progress = Math.min((now - startedAt) / duration, 1);
		const eased = 1 - Math.pow(1 - progress, 3);
		node.textContent = progress === 1 ? parts.original : renderCounterValue(parts, parts.target * eased);
		if (progress < 1) globalThis.requestAnimationFrame(tick);
	};

	globalThis.requestAnimationFrame(tick);
};

const bindMotionSystem = (): void => {
	const root = document.documentElement;
	const motionNodes = new Set<HTMLElement>();

	document.querySelectorAll<HTMLElement>('.reveal, [data-rt-reveal]').forEach((node) => {
		configureMotionNode(node);
		motionNodes.add(node);
	});

	const splitSelectors = [
		'.corporate-split-heading',
		'.academy-page__method-grid',
		'.company-page__base-grid',
		'.company-page__social-grid',
		'.contact-page__main-grid',
		'.feature-split',
		'.product-grid',
		'.cta-grid',
	];
	document.querySelectorAll<HTMLElement>(splitSelectors.join(',')).forEach((group) => {
		Array.from(group.children).forEach((child, index) => {
			if (!(child instanceof HTMLElement)) return;
			configureMotionNode(child, index === 0 ? 'left' : 'right', index);
			motionNodes.add(child);
		});
	});

	const gridSelectors = [
		'.company-page__service-grid',
		'.company-page__expertise',
		'.contact-page__routing-grid',
		'.academy-page__credential-grid',
		'.academy-page__gallery',
		'.rt-courses-grid',
		'.accessories-models',
		'.accessories-series-grid',
		'.accessory-family-grid',
		'.accessory-catalog-grid',
		'.drone-catalog-grid',
		'.drone-related-grid',
		'.accessory-single__related-grid',
		'.drone-archive-cta__grid',
		'.acc-grid',
		'.casos-grid',
		'.intel-grid',
		'.t55-media-grid',
	];
	document.querySelectorAll<HTMLElement>(gridSelectors.join(',')).forEach((group) => {
		Array.from(group.children).forEach((child, index) => {
			if (!(child instanceof HTMLElement)) return;
			configureMotionNode(child, 'up', index % 4);
			motionNodes.add(child);
		});
	});

	document.querySelectorAll<HTMLElement>(
		'.corporate-hero__inner, .drone-archive-hero__copy, .contact-page__hero-grid > *, .hero__content, .hero-grid > *, .accessories-hero, .accessories-catalog-head, .accessories-filters, .section-head, .corporate-cta__inner, .cta-final .cta-inner, .drone-archive-cta > .wrap',
	).forEach((node) => {
		configureMotionNode(node);
		motionNodes.add(node);
	});

	document.querySelectorAll<HTMLElement>(
		'.hero-hex-wrap--video, .contact-page__hero-hex, .corporate-hero__media, .drone-archive-hero__media, .feature-visual, .academy-page__method-media, .company-page__base-media',
	).forEach((node) => {
		configureMotionNode(node, 'scale');
		motionNodes.add(node);
	});

	const counterNodes = new Set<HTMLElement>();
	document.querySelectorAll<HTMLElement>(
		'[data-rt-counter], .statsbar .stat-item__val, .feature-stats .fstat__val, .hud-stats .v',
	).forEach((node) => {
		if (!parseCounter(node)) return;
		node.dataset.rtCounter = node.dataset.rtCounter || 'auto';
		counterNodes.add(node);
	});

	if (reducedMotion.matches || !('IntersectionObserver' in globalThis)) {
		root.classList.add('rt-motion-static');
		motionNodes.forEach((node) => node.classList.add('is-visible'));
		counterNodes.forEach((node) => {
			node.dataset.rtCounted = '1';
		});
		return;
	}

	root.classList.add('rt-motion-ready');
	const observer = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				if (!entry.isIntersecting) return;
				const node = entry.target as HTMLElement;
				if (node.dataset.rtReveal) node.classList.add('is-visible');
				if (node.dataset.rtCounter) animateCounter(node);
				observer.unobserve(node);
			});
		},
		{ rootMargin: '0px 0px -8% 0px', threshold: 0.08 },
	);

	new Set<HTMLElement>([...motionNodes, ...counterNodes]).forEach((node) => observer.observe(node));
};

const bindMenuToggle = (): void => {
	const btn = document.querySelector<HTMLButtonElement>('.nav-toggle');
	const nav = document.querySelector<HTMLElement>('.main-nav');
	if (!btn || !nav) return;

	// The nav is positioned right below the (fixed) header; measuring its own
	// top once visible tells us exactly how much viewport remains below it,
	// so extremely tall menus scroll internally instead of getting clipped
	// off the bottom of the screen.
	const syncMaxHeight = (): void => {
		const top = nav.getBoundingClientRect().top;
		const maxHeight = Math.max(200, window.innerHeight - top - 12);
		document.documentElement.style.setProperty('--rt-nav-max-h', `${maxHeight}px`);
	};

	const closeMenu = (): void => {
		nav.classList.remove('is-open');
		btn.setAttribute('aria-expanded', 'false');
	};

	btn.addEventListener('click', (event) => {
		event.stopPropagation();
		const isOpen = nav.classList.toggle('is-open');
		btn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
		if (isOpen) syncMaxHeight();
	});

	document.addEventListener('pointerdown', (event) => {
		const target = event.target as HTMLElement | null;
		if (!target || nav.contains(target) || btn.contains(target)) return;
		closeMenu();
	});

	document.addEventListener('keydown', (event) => {
		if (event.key === 'Escape') {
			closeMenu();
		}
	});

	window.addEventListener('resize', () => {
		if (window.innerWidth > 860) {
			closeMenu();
		} else if (nav.classList.contains('is-open')) {
			syncMaxHeight();
		}
	});
};

const bindHeaderCondense = (): void => {
	const header = document.querySelector<HTMLElement>('.rt-site-header');
	if (!header) return;

	const sync = (): void => {
		header.classList.toggle('rt-scrolled', window.scrollY > 20);
	};

	sync();
	window.addEventListener('scroll', sync, { passive: true });
	window.addEventListener('resize', sync, { passive: true });
};

const bindProductGallery = (): void => {
	const mainImg = document.getElementById('galleryMain') as HTMLImageElement | null;
	if (!mainImg) return;
	document.querySelectorAll<HTMLElement>('#productGallery .thumb-row .ph').forEach((thumb) => {
		thumb.addEventListener('click', () => {
			const src = thumb.dataset.src;
			const alt = thumb.dataset.alt || '';
			if (!src) return;
			if (mainImg.src.endsWith(src.replace(/^\.?\//, ''))) return;
			mainImg.style.opacity = '0';
			setTimeout(() => {
				mainImg.src = src;
				mainImg.alt = alt;
				mainImg.style.opacity = '1';
			}, 180);
			document.querySelectorAll('#productGallery .thumb-row .ph').forEach((t) => t.classList.remove('active'));
			thumb.classList.add('active');
		});
	});
};

const bindMeshFocus = (): void => {
	if (window.matchMedia('(pointer: coarse)').matches || reducedMotion.matches) return;

	const targets = [
		...Array.from(document.querySelectorAll<HTMLElement>('.hex-overlay-el')).map((mesh) => ({
			host: mesh.closest<HTMLElement>('section, article') ?? mesh.parentElement,
			effect: mesh,
		})),
		...Array.from(document.querySelectorAll<HTMLElement>('.cta-final')).map((cta) => ({
			host: cta,
			effect: cta,
		})),
	];

	targets.forEach(({ host, effect }) => {
		if (!host) return;
		effect.classList.add('has-mesh-focus');
		host.addEventListener('pointerenter', () => effect.classList.add('is-pointer-active'));
		host.addEventListener('pointerleave', () => effect.classList.remove('is-pointer-active'));
		host.addEventListener('pointermove', (event) => {
			const bounds = host.getBoundingClientRect();
			const pointerX = ((event.clientX - bounds.left) / bounds.width) * 100;
			const pointerY = ((event.clientY - bounds.top) / bounds.height) * 100;
			effect.style.setProperty('--mesh-focus-x', `${pointerX}%`);
			effect.style.setProperty('--mesh-focus-y', `${pointerY}%`);
		}, { passive: true });
	});
};

const bindFleetSlider = (): void => {
	const sliders = Array.from(document.querySelectorAll<HTMLElement>('[data-fleet-slider]'));
	if (!sliders.length) return;

	sliders.forEach((slider) => {
		const track = slider.querySelector<HTMLElement>('.fleet-track');
		if (!track) return;

		const slides = Array.from(track.querySelectorAll<HTMLElement>('.fleet-slide'));
		if (slides.length <= 1) return;

		const dotsHost = slider.querySelector<HTMLElement>('[data-fleet-dots]');
		const prev = slider.querySelector<HTMLButtonElement>('[data-fleet-prev]');
		const next = slider.querySelector<HTMLButtonElement>('[data-fleet-next]');

		let index = 0;
		let timer: number | null = null;

		const dots: HTMLButtonElement[] = [];
		if (dotsHost) {
			dotsHost.innerHTML = '';
			slides.forEach((_, i) => {
				const dot = document.createElement('button');
				dot.type = 'button';
				dot.className = `fleet-dot${i === 0 ? ' is-active' : ''}`;
				dot.setAttribute('aria-label', `Ir al slide ${i + 1}`);
				dot.addEventListener('click', () => goTo(i, true));
				dotsHost.appendChild(dot);
				dots.push(dot);
			});
		}

		const render = (): void => {
			track.style.transform = `translateX(-${index * 100}%)`;
			dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
		};

		const goTo = (nextIndex: number, restart = false): void => {
			index = (nextIndex + slides.length) % slides.length;
			render();
			if (restart) {
				stop();
				start();
			}
		};

		const stop = (): void => {
			if (timer !== null) {
				globalThis.clearInterval(timer);
				timer = null;
			}
		};

		const start = (): void => {
			stop();
			if (reducedMotion.matches) return;
			timer = globalThis.setInterval(() => {
				goTo(index + 1);
			}, 5000);
		};

		prev?.addEventListener('click', () => goTo(index - 1, true));
		next?.addEventListener('click', () => goTo(index + 1, true));

		slider.addEventListener('mouseenter', stop);
		slider.addEventListener('mouseleave', start);
		slider.addEventListener('focusin', stop);
		slider.addEventListener('focusout', start);

		render();
		start();
	});
};

const bindDroneSectionOrder = (): void => {
	const root = document.querySelector<HTMLElement>('.drone-single-t50');
	if (!root) return;

	const recambios = root.querySelector<HTMLElement>('.s-recambios');
	const faqs = root.querySelector<HTMLElement>('.s-faqs');
	const cta = root.querySelector<HTMLElement>('.s-cta');
	if (!recambios || !faqs || !cta) return;

	const recPos = recambios.compareDocumentPosition(cta);
	const ctaPosVsFaq = cta.compareDocumentPosition(faqs);
	const ctaBeforeFaq = (ctaPosVsFaq & Node.DOCUMENT_POSITION_FOLLOWING) === Node.DOCUMENT_POSITION_FOLLOWING;
	const ctaAfterRec = (recPos & Node.DOCUMENT_POSITION_FOLLOWING) === Node.DOCUMENT_POSITION_FOLLOWING;

	if (ctaBeforeFaq && ctaAfterRec) {
		faqs.insertAdjacentElement('afterend', cta);
	}
};

const bindVideoLightbox = (): void => {
	const root = document.querySelector<HTMLElement>('.drone-single-t50');
	if (!root) return;

	const modal = root.querySelector<HTMLElement>('[data-rt-video-lightbox]');
	const mediaHost = modal?.querySelector<HTMLElement>('[data-rt-video-container]') ?? null;
	if (!modal || !mediaHost) return;

	const close = (): void => {
		modal.hidden = true;
		document.body.classList.remove('rt-video-open');
		mediaHost.innerHTML = '';
	};

	const toEmbedUrl = (url: string): string => {
		try {
			const parsed = new URL(url);
			const host = parsed.hostname.replace(/^www\./, '');

			if (host === 'youtu.be') {
				const id = parsed.pathname.replace(/^\//, '').trim();
				if (id) return `https://www.youtube.com/embed/${id}?autoplay=1`;
			}

			if (host === 'youtube.com' || host === 'm.youtube.com') {
				const watchId = parsed.searchParams.get('v') || '';
				if (watchId) return `https://www.youtube.com/embed/${watchId}?autoplay=1`;
				if (parsed.pathname.startsWith('/embed/')) {
					return parsed.toString();
				}
			}

			if (host === 'vimeo.com') {
				const id = parsed.pathname.replace(/^\//, '').trim();
				if (id) return `https://player.vimeo.com/video/${id}?autoplay=1`;
			}
		} catch {
			return url;
		}

		return url;
	};

	const open = (videoUrl: string, poster: string, title: string): void => {
		if (!videoUrl) return;

		const isDirectVideo = /\.(mp4|webm|ogg)(\?|$)/i.test(videoUrl);
		if (isDirectVideo) {
			const video = document.createElement('video');
			video.controls = true;
			video.autoplay = true;
			video.preload = 'metadata';
			video.playsInline = true;
			video.setAttribute('aria-label', title || 'Video');
			if (poster) video.poster = poster;
			const source = document.createElement('source');
			source.src = videoUrl;
			video.appendChild(source);
			mediaHost.innerHTML = '';
			mediaHost.appendChild(video);
		} else {
			const frame = document.createElement('iframe');
			frame.src = toEmbedUrl(videoUrl);
			frame.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
			frame.allowFullscreen = true;
			frame.loading = 'lazy';
			frame.referrerPolicy = 'strict-origin-when-cross-origin';
			frame.title = title || 'Video';
			mediaHost.innerHTML = '';
			mediaHost.appendChild(frame);
		}

		modal.hidden = false;
		document.body.classList.add('rt-video-open');
	};

	root.querySelectorAll<HTMLElement>('.js-video-lightbox-trigger').forEach((trigger) => {
		trigger.addEventListener('click', (event) => {
			event.preventDefault();
			const videoUrl = trigger.dataset.videoUrl || '';
			const poster = trigger.dataset.videoPoster || '';
			const title = trigger.dataset.videoTitle || '';
			open(videoUrl, poster, title);
		});
	});

	modal.querySelectorAll<HTMLElement>('[data-rt-video-close]').forEach((closer) => {
		closer.addEventListener('click', (event) => {
			event.preventDefault();
			close();
		});
	});

	document.addEventListener('keydown', (event) => {
		if (event.key === 'Escape' && !modal.hidden) {
			close();
		}
	});
};

const bindAccessoryCatalog = (): void => {
	const catalog = document.querySelector<HTMLElement>('[data-accessory-catalog]');
	if (!catalog) return;

	const form = catalog.querySelector<HTMLFormElement>('.accessories-filters');
	form?.querySelectorAll<HTMLSelectElement>('select').forEach((select) => {
		select.addEventListener('change', () => form.requestSubmit());
	});

	const targetId = decodeURIComponent(globalThis.location.hash.slice(1));
	if (!targetId) return;
	const target = document.getElementById(targetId);
	if (!target?.classList.contains('accessory-catalog-card')) return;
	globalThis.requestAnimationFrame(() => {
		const headerHeight = document.querySelector<HTMLElement>('.rt-site-header')?.offsetHeight ?? 0;
		const targetTop = target.getBoundingClientRect().top + globalThis.scrollY - headerHeight - 24;
		globalThis.scrollTo({ top: Math.max(0, targetTop), behavior: 'auto' });
		target.focus({ preventScroll: true });
		target.classList.add('is-target');
	});
};

const bindDeferredEmbeds = (): void => {
	const embeds = Array.from(document.querySelectorAll<HTMLIFrameElement>('iframe[data-embed-src]'));
	if (!embeds.length) return;

	const loadEmbed = (frame: HTMLIFrameElement): void => {
		const src = frame.dataset.embedSrc || '';
		if (!src || frame.src) return;
		frame.addEventListener(
			'load',
			() => {
				frame.closest<HTMLElement>('.company-page__social-feed')?.classList.add('is-loaded');
				frame.parentElement?.classList.add('is-loaded');
			},
			{ once: true },
		);
		frame.src = src;
	};

	if ('IntersectionObserver' in globalThis) {
		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (!entry.isIntersecting) return;
					const frame = entry.target as HTMLIFrameElement;
					loadEmbed(frame);
					observer.unobserve(frame);
				});
			},
			{ rootMargin: '180px 0px' },
		);
		embeds.forEach((frame) => observer.observe(frame));
		return;
	}

	embeds.forEach((frame) => loadEmbed(frame));
};

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => {
		bindMotionSystem();
		bindHeaderCondense();
		bindMenuToggle();
		bindProductGallery();
		bindMeshFocus();
		bindFleetSlider();
		bindDroneSectionOrder();
		bindVideoLightbox();
		bindAccessoryCatalog();
		bindDeferredEmbeds();
	});
} else {
	bindMotionSystem();
	bindHeaderCondense();
	bindMenuToggle();
	bindProductGallery();
	bindMeshFocus();
	bindFleetSlider();
	bindDroneSectionOrder();
	bindVideoLightbox();
	bindAccessoryCatalog();
	bindDeferredEmbeds();
}

export {};
